import React from 'react'

import { useFormContext, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AlegraLocationOption } from '@/core/hooks/payment/useAlegraLocations'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import { Skeleton } from '@/shared/components/atoms/Skeleton'

import { BillingInformationFormData } from '../BillingInformation.types'

export interface AlegraEspAddressFieldsProps {
	provinces: AlegraLocationOption[]
	cities: AlegraLocationOption[]
	isLoadingCities: boolean
	selectedProvinceId: number | undefined
}

export const AlegraEspAddressFields: React.FC<AlegraEspAddressFieldsProps> = ({
	provinces,
	cities,
	isLoadingCities,
	selectedProvinceId,
}) => {
	const { t } = useTranslation('common')
	const {
		control,
		setValue,
		formState: { errors },
	} = useFormContext<BillingInformationFormData>()

	return (
		<>
			{/* State/Province */}
			<div className="flex flex-col gap-2">
				<Label htmlFor="state">
					{t('account.billing_information.state')} *
				</Label>
				<Controller
					name="state"
					control={control}
					render={({ field }) => (
						<Select
							value={field.value || ''}
							onValueChange={(value) => {
								field.onChange(value)
								setValue('city', '')
							}}
						>
							<SelectTrigger
								className="bg-input w-full"
								aria-invalid={!!errors.state}
							>
								<SelectValue
									placeholder={t('account.billing_information.select_province')}
								/>
							</SelectTrigger>
							<SelectContent>
								{provinces.map((province) => (
									<SelectItem key={province.id} value={province.value}>
										{province.value}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
				/>
				{errors.state && (
					<p className="text-sm text-destructive">{errors.state.message}</p>
				)}
			</div>

			{/* City */}
			<div className="flex flex-col gap-2">
				<Label htmlFor="city">{t('account.billing_information.city')} *</Label>
				{isLoadingCities ? (
					<Skeleton className="h-10 w-full" />
				) : (
					<Controller
						name="city"
						control={control}
						render={({ field }) => (
							<Select
								value={field.value || ''}
								onValueChange={field.onChange}
								disabled={!selectedProvinceId || isLoadingCities}
							>
								<SelectTrigger
									className="bg-input w-full"
									aria-invalid={!!errors.city}
								>
									<SelectValue
										placeholder={t('account.billing_information.select_city')}
									/>
								</SelectTrigger>
								<SelectContent>
									{cities.map((city) => (
										<SelectItem key={city.id} value={city.value}>
											{city.value}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
					/>
				)}
				{errors.city && (
					<p className="text-sm text-destructive">{errors.city.message}</p>
				)}
			</div>

			{/* ZIP Code - Plain input for Spain */}
			<div className="flex flex-col gap-2">
				<Label htmlFor="zip">{t('account.billing_information.zip')} *</Label>
				<Controller
					name="zip"
					control={control}
					render={({ field }) => (
						<Input
							id="zip"
							{...field}
							className="bg-input"
							aria-invalid={!!errors.zip}
						/>
					)}
				/>
				{errors.zip && (
					<p className="text-sm text-destructive">{errors.zip.message}</p>
				)}
			</div>
		</>
	)
}
